import {
    categoryNavigationQuery,
    departmentFromPath,
    hasPlpFilters,
    navigationFilterQuery,
    PLP_FILTER_KEYS,
} from '~/utils/plpNavigation';
import type { RouteLocationRaw } from 'vue-router';

type Availability = {
    isListing?: boolean;
    total: number | null;
    query: Record<string, string>;
    department?: string;
    expires: number;
};
type Probe = { categoryId?: string; categoryPath?: string; search?: string };
type Job = {
    source: Probe;
    key: string;
    probe: Probe;
    query: Record<string, string>;
    department?: string;
    current: () => boolean;
    done: (result: Availability) => void;
};
// Per Nuxt app: never share visitor state between SSR requests.
const queues = new WeakMap<
    object,
    { jobs: Map<string, Job[]>; inFlight: Map<string, Job[]>; timer?: ReturnType<typeof setTimeout>; running: number }
>();

export function useCategoryNavigation(target: Ref<RouteLocationRaw | undefined>, descriptor: Ref<Probe>, visible: Ref<boolean> = ref(true)) {
    const enabled = useKeepFilters();
    const route = useRoute();
    const router = useRouter();
    const app = useNuxtApp();
    const { apiClient } = useShopwareContext();
    const seoMap = useState<Record<string, string>>('categorySeoMap', () => ({}));
    const source = useState<any>('plp-navigation-source', () => null);
    const cache = useState<Record<string, Availability>>('plp-navigation-counts', () => ({}));
    const mounted = ref(false);
    onMounted(() => {
        mounted.value = true;
    });
    const resolved = computed(() => {
        if (!target.value || (typeof target.value === 'string' && /^(?:https?:|mailto:|tel:|#)/.test(target.value)))
            return null;
        return router.resolve(target.value);
    });
    const sourceDepartment = computed(
        () =>
            departmentFromPath(route.path) || (source.value?.path === route.path ? source.value.department : undefined),
    );
    const probe = computed<Probe>(() => {
        const path = resolved.value?.path.replace(/\/$/, '');
        const brandTarget = source.value?.path === route.path ? source.value?.brandRoutes?.[path || ''] : undefined;
        const id =
            descriptor.value.categoryId ||
            brandTarget?.categoryId ||
            Object.entries(seoMap.value).find(
                ([, url]) => '/' + url.replace(/^\//, '').replace(/\/$/, '') === path,
            )?.[0];
        return {
            ...(id ? { categoryId: id } : {}),
            ...(descriptor.value.categoryPath ? { categoryPath: descriptor.value.categoryPath } : {}),
            ...(descriptor.value.search ||
            brandTarget?.search ||
            (resolved.value?.path.replace(/\/$/, '') === '/search' ? (resolved.value.query.tn_q ?? route.query.tn_q) : '')
                ? { search: String(descriptor.value.search || brandTarget?.search || resolved.value?.query.tn_q || route.query.tn_q) }
                : {}),
        };
    });
    const isCategory = computed(() => !!(probe.value.categoryId || probe.value.categoryPath || probe.value.search));
    const query = computed(() =>
        categoryNavigationQuery(
            route.query,
            resolved.value?.path.replace(/\/$/, '') === '/search'
                ? { tn_q: route.query.tn_q, ...resolved.value.query }
                : resolved.value?.query || {},
            sourceDepartment.value,
            departmentFromPath(resolved.value?.path || ''),
        ),
    );
    const sourceTarget = computed<Probe>(() => ({
        ...(!route.query.tn_cid && source.value?.path === route.path && source.value?.categoryId
            ? { categoryId: source.value.categoryId }
            : {}),
        ...(route.query.tn_cid || (source.value?.path === route.path && source.value?.categoryPath)
            ? { categoryPath: String(route.query.tn_cid || source.value.categoryPath) }
            : {}),
    }));
    const filters = computed(() => navigationFilterQuery(query.value));
    const needsCheck = computed(() => enabled.value && isCategory.value && hasPlpFilters(route.query));
    const key = computed(() =>
        JSON.stringify([probe.value, filters.value, sourceDepartment.value, sourceTarget.value]),
    );
    const result = ref<Availability | null>(null);
    const checking = ref(false);
    let generation = 0;
    onBeforeUnmount(() => {
        generation++;
    });

    async function drain() {
        const queue = queues.get(app)!;
        for (const [key, jobs] of queue.jobs) {
            const active = jobs.filter(job => job.current());
            if (active.length) queue.jobs.set(key, active);
            else queue.jobs.delete(key);
        }
        if (queue.running >= 2 || !queue.jobs.size) return;
        queue.running++;
        // Only batch requests sharing a filter/department context.
        const first = queue.jobs.values().next().value![0];
        const context = JSON.stringify([first.query, first.department, first.source]);
        const batch = [...queue.jobs.entries()]
            .filter(([, jobs]) => JSON.stringify([jobs[0].query, jobs[0].department, jobs[0].source]) === context)
            .slice(0, 8);
        batch.forEach(([key, jobs]) => {
            queue.jobs.delete(key);
            queue.inFlight.set(key, jobs);
        });
        // Keep independent batches moving; a slow header must not block the sidebar.
        if (queue.jobs.size) void drain();
        let targets: Record<string, any> = {};
        try {
            const { data } = await apiClient.invoke(
                'CategoryNavigation post /tweakwise/category-navigation' as any,
                {
                    body: {
                        targets: Object.fromEntries(batch.map(([, jobs], i) => [String(i), jobs[0].probe])),
                        query: first.query,
                        department: first.department,
                        source: first.source,
                    },
                } as any,
            );
            targets = (data as any)?.targets || {};
        } catch {
            /* Unknown availability stays disabled and exposes retry. */
        }
        batch.forEach(([key, jobs], i) => {
            queue.inFlight.delete(key);
            const response = targets[String(i)];
            const value: Availability = {
                isListing: response?.isListing !== false,
                total: typeof response?.total === 'number' ? response.total : null,
                query: response?.query || jobs[0].query,
                department: response?.department,
                expires: Date.now() + 30_000,
            };
            jobs.forEach(job => {
                if (value.total !== null || value.isListing === false) cache.value[job.key] = value;
                job.done(value);
            });
        });
        // Bound memory during long shopping sessions.
        for (const [key, value] of Object.entries(cache.value)) if (value.expires < Date.now()) delete cache.value[key];
        queue.running--;
        if (queue.jobs.size) void drain();
    }

    function check(force = false) {
        const gen = ++generation;
        result.value = null;
        checking.value = false;
        if (!mounted.value || !visible.value || !needsCheck.value) return;
        const cached = cache.value[key.value];
        if (!force && cached && cached.expires > Date.now()) {
            result.value = cached;
            return;
        }
        checking.value = true;
        if (!queues.has(app)) queues.set(app, { jobs: new Map(), inFlight: new Map(), running: 0 });
        const queue = queues.get(app)!;
        const existingRequest = queue.inFlight.get(key.value);
        const jobs = existingRequest || queue.jobs.get(key.value) || [];
        jobs.push({
            source: sourceTarget.value,
            key: key.value,
            probe: probe.value,
            query: filters.value,
            department: sourceDepartment.value,
            current: () => gen === generation && visible.value && needsCheck.value,
            done: value => {
                if (gen !== generation) return;
                result.value = value;
                checking.value = false;
            },
        });
        // A newly mounted or visible copy can subscribe while the original batch
        // is already running. It receives the same result without another request.
        if (existingRequest) return;
        queue.jobs.set(key.value, jobs);
        if (queue.timer) clearTimeout(queue.timer);
        queue.timer = setTimeout(() => void drain(), 30);
    }
    watch([key, needsCheck, mounted, visible], () => check(), { immediate: true });
    const to = computed(() => {
        if (!enabled.value || !isCategory.value || !resolved.value || result.value?.isListing === false) return target.value;
        const next = { ...query.value };
        if (result.value?.total != null) {
            for (const key of PLP_FILTER_KEYS) delete next[key];
            Object.assign(next, result.value.query);
        }
        return { path: resolved.value.path, query: next, hash: resolved.value.hash };
    });
    return {
        to,
        disabled: computed(
            () =>
                needsCheck.value &&
                result.value?.isListing !== false &&
                (result.value?.total == null || result.value.total === 0),
        ),
        pending: computed(() => needsCheck.value && (!mounted.value || checking.value || !result.value)),
        failed: computed(
            () => needsCheck.value && !!result.value && result.value.isListing !== false && result.value.total === null,
        ),
        retry: () => check(true),
    };
}
